<template>
  <div class="survey-field-wrapper">
    <!-- Field Question/Label -->
    <label :for="field.id" class="block text-xs text-gray-500 mb-1">
      {{ field.question }}
      <span v-if="field.required" class="text-red-500">*</span>
    </label>

    <!-- Text Input -->
    <input
      v-if="field.type === 'text'"
      :id="field.id"
      :value="modelValue"
      type="text"
      :placeholder="field.placeholder"
      :required="field.required"
      class="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
      @input="updateValue($event.target.value)"
      @blur="validateField"
    />

    <!-- Textarea -->
    <textarea
      v-else-if="field.type === 'textarea'"
      :id="field.id"
      :value="modelValue"
      :placeholder="field.placeholder"
      :required="field.required"
      rows="2"
      class="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
      @input="updateValue($event.target.value)"
      @blur="validateField"
    ></textarea>

    <!-- Number Input -->
    <input
      v-else-if="field.type === 'number'"
      :id="field.id"
      :value="modelValue"
      type="number"
      :placeholder="field.placeholder"
      :required="field.required"
      :min="field.validation?.min"
      :max="field.validation?.max"
      class="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
      @input="updateValue(Number($event.target.value))"
      @blur="validateField"
    />

    <!-- Date Input -->
    <input
      v-else-if="field.type === 'date'"
      :id="field.id"
      :value="modelValue"
      type="date"
      :required="field.required"
      class="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
      @input="updateValue($event.target.value)"
      @blur="validateField"
    />

    <!-- Radio Buttons -->
    <div v-else-if="field.type === 'radio'" class="space-y-1">
      <div v-for="option in field.options" :key="option.value"
           class="flex items-center px-2.5 py-1.5 border border-gray-200 rounded hover:bg-gray-50 cursor-pointer"
           :class="{ 'border-primary-500 bg-primary-50': modelValue === option.value }"
           @click="updateValue(option.value)">
        <input
          :id="`${field.id}_${option.value}`"
          type="radio"
          :name="field.id"
          :value="option.value"
          :checked="modelValue === option.value"
          :required="field.required"
          class="h-3 w-3 text-primary-600 border-gray-300 focus:ring-primary-500"
          @change="updateValue(option.value)"
        />
        <label :for="`${field.id}_${option.value}`" class="ml-2 cursor-pointer flex-1">
          <span class="text-xs text-gray-900">{{ option.label }}</span>
        </label>
        <span v-if="option.score !== undefined && option.score !== null" class="text-xs text-gray-400">
          {{ option.score }}
        </span>
      </div>
    </div>

    <!-- Checkboxes -->
    <div v-else-if="field.type === 'checkbox'" class="space-y-1">
      <div v-for="option in field.options" :key="option.value"
           class="flex items-start px-2.5 py-1.5 border border-gray-200 rounded hover:bg-gray-50">
        <input
          :id="`${field.id}_${option.value}`"
          type="checkbox"
          :value="option.value"
          :checked="Array.isArray(modelValue) && modelValue.includes(option.value)"
          class="h-3 w-3 text-primary-600 border-gray-300 rounded focus:ring-primary-500 mt-0.5"
          @change="handleCheckboxChange(option.value, $event.target.checked)"
        />
        <label :for="`${field.id}_${option.value}`" class="ml-2 cursor-pointer flex-1">
          <span class="text-xs text-gray-900">{{ option.label }}</span>
        </label>
      </div>
    </div>

    <!-- Select Dropdown -->
    <select
      v-else-if="field.type === 'select'"
      :id="field.id"
      :value="modelValue"
      :required="field.required"
      class="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
      @change="handleSelectChange($event.target.value)"
    >
      <option value="">Select...</option>
      <option v-for="option in field.options" :key="option.value" :value="option.value">
        {{ option.label }}
      </option>
    </select>

    <!-- Scale (Rating) -->
    <div v-else-if="field.type === 'scale'" class="space-y-1">
      <div
        v-for="option in field.options"
        :key="option.value"
        class="flex items-center px-2.5 py-1.5 border border-gray-200 rounded hover:bg-gray-50 cursor-pointer transition-colors"
        :class="{ 'border-primary-500 bg-primary-50': modelValue === option.value }"
        @click="updateValue(option.value)"
      >
        <input
          :id="`${field.id}_${option.value}`"
          type="radio"
          :name="field.id"
          :value="option.value"
          :checked="modelValue === option.value"
          :required="field.required"
          class="h-3 w-3 text-primary-600 border-gray-300 focus:ring-primary-500"
          @change="updateValue(option.value)"
        />
        <label :for="`${field.id}_${option.value}`" class="ml-2 cursor-pointer flex-1">
          <span class="text-xs text-gray-900">{{ option.label }}</span>
        </label>
        <span v-if="option.score !== undefined && option.score !== null" class="text-xs text-gray-400">
          {{ option.score }}
        </span>
      </div>
    </div>

    <!-- Boolean (Yes/No) -->
    <div v-else-if="field.type === 'boolean' || field.type === 'yesno'" class="flex gap-2">
      <button
        @click="updateValue(true)"
        :class="[
          'px-3 py-1.5 rounded text-xs font-medium transition-colors',
          modelValue === true
            ? 'bg-green-600 text-white'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        ]"
      >Yes</button>
      <button
        @click="updateValue(false)"
        :class="[
          'px-3 py-1.5 rounded text-xs font-medium transition-colors',
          modelValue === false
            ? 'bg-red-600 text-white'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        ]"
      >No</button>
    </div>

    <!-- Nested List (repeatable entries) -->
    <div v-else-if="field.type === 'nested_list'" class="space-y-2">
      <div v-if="Array.isArray(modelValue) && modelValue.length > 0" class="space-y-2">
        <div v-for="(entry, entryIndex) in modelValue" :key="entryIndex" class="p-2.5 border border-gray-200 rounded bg-gray-50">
          <div class="flex items-center justify-between mb-2">
            <span class="text-xs font-medium text-gray-500">Entry {{ entryIndex + 1 }}</span>
            <button type="button" class="text-xs text-red-500 hover:text-red-600" @click="removeNestedEntry(entryIndex)">
              <FontAwesomeIcon :icon="['fas', 'trash-can']" class="text-xs" />
            </button>
          </div>
          <div class="space-y-2">
            <SurveyFieldRenderer
              v-for="childField in orderedChildFields"
              :key="childField.id"
              :field="childField"
              :model-value="entry[childField.id]"
              @update:model-value="updateNestedEntry(entryIndex, childField.id, $event)"
            />
          </div>
        </div>
      </div>
      <div v-else class="text-center py-2">
        <p class="text-xs text-gray-400">No entries yet</p>
      </div>
      <button type="button" class="text-xs text-primary-600 hover:text-primary-700 font-medium" @click="addNestedEntry">+ Add</button>
    </div>

    <!-- Field Validation Error -->
    <p v-if="error" class="mt-1 text-xs text-red-600">
      {{ error }}
    </p>
  </div>
</template>

<script>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

export default {
  name: 'SurveyFieldRenderer',

  components: {
    FontAwesomeIcon,
  },

  props: {
    field: {
      type: Object,
      required: true
    },
    modelValue: {
      required: false,
      default: null
    },
    error: {
      type: String,
      default: null
    }
  },

  emits: ['update:modelValue', 'validate'],

  computed: {
    orderedChildFields() {
      const children = this.field.fields || [];
      return [...children].sort((a, b) => (a.order || 0) - (b.order || 0));
    },
  },

  methods: {
    updateValue(value) {
      this.$emit('update:modelValue', value);
    },

    buildNestedEntry() {
      const entry = {};
      (this.field.fields || []).forEach(child => {
        if (child.type === 'checkbox') {
          entry[child.id] = [];
        } else if (child.type === 'number' || child.type === 'boolean' || child.type === 'scale') {
          entry[child.id] = null;
        } else {
          entry[child.id] = '';
        }
      });
      return entry;
    },

    addNestedEntry() {
      const list = Array.isArray(this.modelValue) ? this.modelValue.map(e => ({ ...e })) : [];
      list.push(this.buildNestedEntry());
      this.updateValue(list);
    },

    removeNestedEntry(index) {
      const list = Array.isArray(this.modelValue) ? this.modelValue.map(e => ({ ...e })) : [];
      list.splice(index, 1);
      this.updateValue(list);
    },

    updateNestedEntry(index, key, value) {
      const list = Array.isArray(this.modelValue) ? this.modelValue.map(e => ({ ...e })) : [];
      if (!list[index]) list[index] = {};
      list[index][key] = value;
      this.updateValue(list);
    },

    handleCheckboxChange(optionValue, isChecked) {
      let currentValue = Array.isArray(this.modelValue) ? [...this.modelValue] : [];

      if (isChecked) {
        if (!currentValue.includes(optionValue)) {
          currentValue.push(optionValue);
        }
      } else {
        currentValue = currentValue.filter(v => v !== optionValue);
      }

      this.updateValue(currentValue);
    },

    handleSelectChange(value) {
      this.updateValue(value);
      this.validateField();
    },

    validateField() {
      this.$emit('validate');
    }
  }
};
</script>

<style scoped>
@reference "../../../assets/theme.css";
.survey-field-wrapper {
  @apply w-full;
}

/* Radio button checked state */
input[type="radio"]:checked {
  border-color: #4f46e5;
  background-color: #4f46e5;
}

/* Checkbox checked state */
input[type="checkbox"]:checked {
  border-color: #4f46e5;
  background-color: #4f46e5;
}


</style>
